import React, { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { FaCreditCard, FaShieldAlt, FaFilter } from 'react-icons/fa';
import displayINRCurrency from '../helpers/displayCurrency';
import moment from 'moment';
import useTranslate from '../hooks/useTranslate';

const dummyPayments = [
    {
        _id: "PAY492819385",
        createdAt: "2024-05-12T10:30:00Z",
        amount: 1845,
        status: "Success",
        method: "UPI / PhonePe",
        orderId: "ORD492819385"
    },
    {
        _id: "PAY129384756",
        createdAt: "2024-04-28T14:15:00Z",
        amount: 2499,
        status: "Success",
        method: "Credit Card",
        orderId: "ORD129384756"
    },
    {
        _id: "PAY998877665",
        createdAt: "2024-05-15T09:45:00Z",
        amount: 550,
        status: "Failed",
        method: "UPI / GPay",
        orderId: "ORD998877665"
    }
];

const PaymentDrawer = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('Status'); // 'Status' or 'Mode'
    const [statusFilter, setStatusFilter] = useState('All');
    const [modeFilter, setModeFilter] = useState('All');

    React.useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const t = useTranslate({
        myPayment: "My Payment",
        viewHistory: "View your transaction history",
        filterStatus: "Filter Status",
        paymentMode: "Payment Mode",
        all: "All",
        success: "Success",
        failed: "Failed",
        upi: "UPI",
        card: "Card",
        txnId: "TXN ID",
        paymentMethod: "Payment Method",
        paidAmount: "Paid Amount",
        securePayment: "100% Secure Payment",
        orderLabel: "Order",
        closeHistory: "Close History",
        noTransactions: "No transactions found"
    });

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'success': return 'text-[#00b259] bg-green-50 border-green-100';
            case 'failed': return 'text-red-500 bg-red-50 border-red-100';
            case 'pending': return 'text-orange-500 bg-orange-50 border-orange-100';
            default: return 'text-gray-500 bg-gray-50 border-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status.toLowerCase()) {
            case 'success': return '✓';
            case 'failed': return '✕';
            case 'pending': return '●';
            default: return '○';
        }
    };

    const filteredPayments = dummyPayments.filter(payment => {
        const matchesStatus = statusFilter === 'All' || payment.status === statusFilter;
        const matchesMode = modeFilter === 'All' || (modeFilter === 'UPI' ? payment.method.includes('UPI') : payment.method.includes('Card'));
        return matchesStatus && matchesMode;
    });

    return (
        <div className='fixed inset-0 z-[100] flex justify-end'>
            <div className='absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn' onClick={onClose}></div>
            <div className='relative w-full max-w-[400px] bg-[#f8fcf9] h-full shadow-2xl flex flex-col animate-slideInRight'>
                {/* Header */}
                <div className='relative bg-[#143d2c] pt-12 pb-8 px-6 overflow-hidden'>
                    <div className='absolute top-0 right-0 w-32 h-32 bg-[#00b259]/10 rounded-full -mr-16 -mt-16 blur-2xl'></div>
                    <button onClick={onClose} className='absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full text-white transition-all'>
                        <MdClose size={24} />
                    </button>
                    <div className='relative z-10 flex flex-col items-center mt-2'>
                        <div className='w-14 h-14 rounded-full bg-white/10 border border-[#f39200] flex items-center justify-center text-[#f39200] shadow-lg backdrop-blur-md overflow-hidden mb-3'>
                            <FaCreditCard size={24} />
                        </div>
                        <h2 className='text-xl font-black text-white tracking-wide'>{t.myPayment}</h2>
                        <p className='text-xs text-[#f39200] font-bold uppercase tracking-widest mt-1'>{t.viewHistory}</p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className='bg-white flex border-b border-gray-100 transition-colors duration-300'>
                    <button
                        onClick={() => setActiveTab('Status')}
                        className={`flex-1 py-3 text-[11px] font-black uppercase tracking-widest transition-all relative ${activeTab === 'Status' ? 'text-[#00b259]' : 'text-gray-400'}`}
                    >
                        {t.filterStatus}
                        {activeTab === 'Status' && <div className='absolute bottom-0 left-0 w-full h-1 bg-[#00b259] rounded-t-full'></div>}
                    </button>
                    <button
                        onClick={() => setActiveTab('Mode')}
                        className={`flex-1 py-3 text-[11px] font-black uppercase tracking-widest transition-all relative ${activeTab === 'Mode' ? 'text-[#00b259]' : 'text-gray-400'}`}
                    >
                        {t.paymentMode}
                        {activeTab === 'Mode' && <div className='absolute bottom-0 left-0 w-full h-1 bg-[#00b259] rounded-t-full'></div>}
                    </button>
                </div>

                {/* Filters */}
                <div className='p-4 bg-white shadow-sm shrink-0'>
                    {activeTab === 'Status' ? (
                        <div className='flex gap-2 animate-fadeIn'>
                            {['All', 'Success', 'Failed'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    className={`flex-1 py-2 px-3 rounded-xl text-[11px] font-black transition-all border-2 ${statusFilter === status
                                        ? 'bg-[#00b259] text-white border-[#00b259] shadow-lg'
                                        : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-100'
                                        }`}
                                >
                                    {t[status.toLowerCase()]?.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className='flex gap-2 animate-fadeIn'>
                            {['All', 'UPI', 'Card'].map((mode) => (
                                <button
                                    key={mode}
                                    onClick={() => setModeFilter(mode)}
                                    className={`flex-1 py-2 px-3 rounded-xl text-[11px] font-black transition-all border-2 ${modeFilter === mode
                                        ? 'bg-[#f39200] text-white border-[#f39200] shadow-lg'
                                        : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-100'
                                        }`}
                                >
                                    {t[mode.toLowerCase()]?.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Transactions List */}
                <div className='flex-1 overflow-y-auto px-4 py-4 custom-scrollbar'>
                    {filteredPayments.length === 0 ? (
                        <div className='flex flex-col items-center justify-center h-full opacity-40'>
                            <FaFilter size={40} className='text-gray-300 mb-2' />
                            <p className='text-xs font-bold uppercase tracking-widest'>{t.noTransactions}</p>
                        </div>
                    ) : (
                        <div className='space-y-4'>
                            {filteredPayments.map((payment) => (
                                <div key={payment._id} className='bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all relative overflow-hidden group'>
                                    <div className='flex justify-between items-start mb-3 border-b border-gray-50 pb-3'>
                                        <div>
                                            <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none'>{t.txnId}: #{payment._id}</p>
                                            <p className='text-[12px] font-black text-[#143d2c] mt-1'>{moment(payment.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
                                        </div>
                                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${getStatusColor(payment.status)}`}>
                                            {getStatusIcon(payment.status)}
                                            {t[payment.status.toLowerCase()]}
                                        </div>
                                    </div>

                                    {/* Payment details */}
                                    <div className='flex justify-between items-center mb-1'>
                                        <div className='flex flex-col'>
                                            <p className='text-[10px] font-bold text-gray-400 uppercase leading-none mb-1'>{t.paymentMethod}</p>
                                            <h3 className='font-bold text-gray-800 text-[13px] leading-tight'>{payment.method}</h3>
                                        </div>
                                        <div className='text-right'>
                                            <p className='text-[10px] font-bold text-gray-400 uppercase leading-none mb-1'>{t.paidAmount}</p>
                                            <p className='text-[16px] font-black text-[#143d2c]'>{displayINRCurrency(payment.amount)}</p>
                                        </div>
                                    </div>

                                    <div className='mt-3 pt-3 border-t border-gray-50 flex items-center justify-between'>
                                        <p className='text-[10px] font-bold text-[#00b259] flex items-center gap-1.5'>
                                            <FaShieldAlt size={10} /> {t.securePayment}
                                        </p>
                                        <p className='text-[10px] font-bold text-gray-400'>{t.orderLabel}: #{payment.orderId}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className='px-6 py-4 bg-white border-t border-gray-100 shrink-0 mt-auto'>
                    <button
                        onClick={onClose}
                        className='w-full bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] hover:brightness-110 text-white font-black py-4 rounded-xl shadow-lg transition-all active:scale-95 text-[11px] uppercase tracking-[3px]'
                    >
                        {t.closeHistory}
                    </button>
                </div>
            </div>

            <style jsx="true">{`
 .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
 .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
 @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
 @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
 .custom-scrollbar::-webkit-scrollbar { width: 4px; }
 .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,178,89,0.1); border-radius: 10px; }
 `}</style>
        </div>
    );
};

export default PaymentDrawer;
